import { useState } from 'react';
import { Dialog, DialogContent, TextField, Button, Stack, Typography, FormControl, Select, MenuItem, Box } from '@mui/material';
import MainCard from 'components/MainCard';
import { useNavigate } from 'react-router-dom';
import { validateUserComment } from 'utils/userComments';

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  onConfirm: (data: { orderId: string; reason: string; comment: string }) => Promise<boolean | void> | boolean | void;
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({ isOpen, onClose, orderId, onConfirm }) => {
  const [reason, setReason] = useState('Product change');
  const [comment, setComment] = useState('');
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const navigate = useNavigate();

  const cancelReasons = [
    'Product change',
    'Out of area',
    'Customer cancellation',
    'Insufficient information/Unable to complete',
    'Duplicate order'
  ];

  const handleInitialConfirm = () => {
    setShowConfirmationPopup(true);
  };

  const handleFinalConfirm = async () => {
    try {
      const didConfirm = await onConfirm({ orderId, reason, comment });
      if (didConfirm === false) {
        return;
      }

      setReason(cancelReasons[0]); // Reset to first option from the array
      setComment('');
      setShowConfirmationPopup(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to cancel order:', error);
    }
  };

  const handleBackToReason = () => {
    setShowConfirmationPopup(false);
  };

  const ConfirmationPopup = () => (
    <Dialog
      open={showConfirmationPopup}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400,
        '& .MuiDialog-paper': {
          borderRadius: 0,
          maxWidth: 450,
          width: '100%',
          m: 2,
          backgroundColor: 'white'
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3,
          textAlign: 'center',
          backgroundColor: 'white'
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: 'rgba(254, 122, 122, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}
        >
          <Typography variant="h1" color="error" sx={{ fontWeight: 'bold' }}>
            !
          </Typography>
        </Box>

        <Typography
          variant="h3"
          sx={{
            mb: 3,
            color: '#303030',
            fontSize: '1.2rem',
            fontWeight: 800
          }}
        >
          Are you sure you want to <br />
          cancel this order?
        </Typography>

        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleBackToReason}
            sx={{
              textTransform: 'none',
              color: '#303030',
              borderColor: '#d0d0d0',
              borderRadius: 0,
              fontSize: '0.9rem',
              '&:hover': {
                borderColor: '#c0c0c0',
                backgroundColor: 'rgba(0, 0, 0, 0.03)'
              }
            }}
          >
            Back
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleFinalConfirm}
            sx={{
              textTransform: 'none',
              backgroundColor: '#008E9C',
              borderRadius: 0,
              fontSize: '0.9rem',
              '&:hover': {
                backgroundColor: '#01707a'
              }
            }}
          >
            Yes
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );

  return (
    <>
      <Dialog
        open={isOpen && !showConfirmationPopup}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 0
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <MainCard title="Cancel Order" sx={{ boxShadow: 'none' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Reason for Cancellation
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <Select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: '0.875rem',
                    borderRadius: 0,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    }
                  }}
                >
                  {cancelReasons.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Comment
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Enter Comment"
                value={comment}
                error={validateUserComment(comment) != null}
                helperText={validateUserComment(comment)}
                onChange={(e) => setComment(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  mb: 3,
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0
                  }
                }}
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderRadius: 0,
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      color: 'text.primary'
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleInitialConfirm}
                  disabled={validateUserComment(comment) != null}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#008E9C',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: '#01707a'
                    }
                  }}
                >
                  Confirm
                </Button>
              </Stack>
            </Box>
          </MainCard>
        </DialogContent>
      </Dialog>

      <ConfirmationPopup />
    </>
  );
};

export default CancelOrderModal;
